import React, { useEffect, useState } from 'react';
import { CreditCard, CheckCircle2, AlertTriangle, Clock, Receipt } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import {
  billing as api,
  residentBilling,
  type Invoice,
  type ResidentBillingSummary,
  type ApiError,
} from '../../services/api';

declare global {
  interface Window {
    Razorpay: new (options: Record<string, unknown>) => { open: () => void };
  }
}

let razorpayScriptPromise: Promise<boolean> | null = null;
function loadRazorpayScript(): Promise<boolean> {
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise(resolve => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
}

const statusColor = (s: string) =>
  s === 'paid' ? { bg: 'rgba(16,185,129,0.1)', text: '#10B981' }
  : s === 'overdue' ? { bg: 'rgba(239,68,68,0.1)', text: '#DC2626' }
  : s === 'waived' ? { bg: 'rgba(148,163,184,0.1)', text: '#64748B' }
  : { bg: 'rgba(245,158,11,0.1)', text: '#D97706' };

const fmtAmount = (n: number | undefined) =>
  `₹${(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MyBillingPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [summary, setSummary] = useState<ResidentBillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [payError, setPayError] = useState('');

  const load = () => {
    if (!societyId) { setLoading(false); return; }
    setLoading(true);
    residentBilling.mySummary(societyId)
      .then(setSummary)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [societyId]);

  const handlePayOnline = async (inv: Invoice) => {
    if (!societyId) return;
    setPayingId(inv.id); setPayError('');
    try {
      const order = await api.createPaymentOrder(societyId, inv.id);
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setPayError('Failed to load payment gateway. Please try again.');
        setPayingId(null);
        return;
      }
      const rzp = new window.Razorpay({
        key: order.keyId,
        amount: Math.round(order.amount * 100),
        currency: order.currency,
        name: 'Nexora',
        description: `Invoice ${inv.invoiceNumber}`,
        order_id: order.orderId,
        prefill: { name: user?.fullName, email: user?.email },
        handler: async (response: { razorpay_payment_id: string; razorpay_signature: string }) => {
          try {
            await api.verifyPayment(societyId, inv.id, {
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            load();
          } catch (err) {
            setPayError((err as ApiError).message || 'Payment verification failed.');
          } finally {
            setPayingId(null);
          }
        },
        modal: { ondismiss: () => setPayingId(null) },
      });
      rzp.open();
    } catch (err) {
      setPayError((err as ApiError).message || 'Failed to start online payment.');
      setPayingId(null);
    }
  };

  const payable = (inv: Invoice) => inv.status === 'unpaid' || inv.status === 'overdue';

  return (
    <PageLayout
      title="My Billing"
      subtitle="View your maintenance charges and pay online"
      icon={<CreditCard size={18} />}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading…</div>
      ) : !summary ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--nx-text-muted)', fontSize: 13 }}>
          Couldn't load your billing details. Please try again later.
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
            <div className="nx-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>This Month</div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 24, fontWeight: 700, color: 'var(--nx-text-primary)' }}>{fmtAmount(summary.currentMonthDue)}</div>
              <span style={{
                display: 'inline-block', marginTop: 8, padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                background: statusColor(summary.currentMonthStatus).bg, color: statusColor(summary.currentMonthStatus).text,
              }}>
                {summary.currentMonthStatus.charAt(0).toUpperCase() + summary.currentMonthStatus.slice(1)}
              </span>
            </div>
            <div className="nx-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Total Outstanding</div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 24, fontWeight: 700, color: summary.totalOutstanding === 0 ? '#10B981' : '#DC2626' }}>
                {fmtAmount(summary.totalOutstanding)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 8 }}>
                {summary.totalOutstanding === 0 ? 'All paid up!' : 'Across all pending invoices'}
              </div>
            </div>
            <div className="nx-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Last Paid</div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--nx-text-primary)' }}>
                {summary.lastPaidAt ? new Date(summary.lastPaidAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
              </div>
            </div>
          </div>

          {/* Current month charge breakdown */}
          {summary.charges.length > 0 && (
            <div className="nx-card" style={{ padding: 20, marginBottom: 20 }}>
              <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 14 }}>
                This Month's Charges
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {summary.charges.map((ch, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: 'var(--nx-text-secondary)' }}>{ch.name}</span>
                    <span style={{ fontWeight: 600, color: 'var(--nx-text-primary)' }}>{fmtAmount(ch.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {payError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, marginBottom: 16 }}>
              <AlertTriangle size={14} style={{ color: '#DC2626', flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#DC2626' }}>{payError}</span>
            </div>
          )}

          {/* Invoice history */}
          <div className="nx-card" style={{ padding: 20 }}>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 16 }}>
              Invoice History
            </h3>
            {summary.invoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <Receipt size={28} style={{ color: 'var(--nx-text-muted)', marginBottom: 10 }} />
                <div style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>No invoices yet.</div>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Invoice', 'Amount', 'Due Date', 'Status', ''].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', color: 'var(--nx-text-muted)', borderBottom: '1px solid var(--nx-surface-3)', textTransform: 'uppercase' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summary.invoices.map(inv => {
                    const sc = statusColor(inv.status);
                    return (
                      <tr key={inv.id}>
                        <td style={{ padding: '10px 10px', color: 'var(--nx-text-primary)', fontWeight: 500 }}>{inv.invoiceNumber}</td>
                        <td style={{ padding: '10px 10px', color: 'var(--nx-text-primary)', fontWeight: 600 }}>{fmtAmount(inv.totalAmount ?? inv.amount)}</td>
                        <td style={{ padding: '10px 10px', color: 'var(--nx-text-muted)' }}>
                          {new Date(inv.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td style={{ padding: '10px 10px' }}>
                          <span style={{ padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 500, background: sc.bg, color: sc.text }}>
                            {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                          </span>
                        </td>
                        <td style={{ padding: '10px 10px' }}>
                          {payable(inv) && (
                            <button
                              className="nx-btn nx-btn-primary nx-btn-sm"
                              onClick={() => handlePayOnline(inv)}
                              disabled={payingId === inv.id}
                              style={{ gap: 5 }}
                            >
                              {payingId === inv.id ? <Clock size={12} /> : <CreditCard size={12} />}
                              {payingId === inv.id ? 'Processing…' : 'Pay Online'}
                            </button>
                          )}
                          {inv.status === 'paid' && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#10B981' }}>
                              <CheckCircle2 size={13} /> Paid
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </PageLayout>
  );
};

export default MyBillingPage;
